/** Shared withdrawal/deposit domain constants (aligned with python-be WD helpers). */

export const WITHDRAWAL_DEPOSIT_REF_PREFIX = "withdrawal_deposit:";
export const WITHDRAWAL_DEPOSIT_COMMENT_ENTITY_PREFIX = "wd-request:";

/** RTK Query virtual list tag for withdrawal/deposit form collections. */
export const WITHDRAWAL_DEPOSIT_FORM_LIST_TAG = "LIST";

export const DEFAULT_TRUSTEE_ORG_LABEL = "Amicorp Trustee";
export const AMICORP_TRUST_LEGACY_LABEL = "Amicorp Trust";
export const TRUSTEE_EWALLET_REF_PREFIX = "EWT-";

export const AMICORP_TRUSTEE_ALIAS_LABELS = new Set([
  "amicorp trust",
  "amicorp",
  "amicorp trustee",
]);

/** Source labels returned by GET /withdrawl-deposit/account-options. */
export const AccountOptionWalletType = {
  SALESFORCE_MIGRATION: "SF_MIGRATION",
  PLATFORM_TRUSTEE: "INCOMLEND_TRUSTEE",
} as const;

export type AccountOptionWalletTypeValue =
  (typeof AccountOptionWalletType)[keyof typeof AccountOptionWalletType];

export const WithdrawalDepositTransferType = {
  WITHDRAWAL: "withdrawal",
  DEPOSIT: "deposit",
} as const;

export type WithdrawalDepositTransferTypeValue =
  (typeof WithdrawalDepositTransferType)[keyof typeof WithdrawalDepositTransferType];

/** Stored workflow statuses on withdrawal_deposits.status. */
export const WithdrawalDepositFormStatus = {
  PENDING: "pending",
  IN_PROGRESS: "in-progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  REJECTED: "rejected",
  FAILED: "failed",
} as const;

export type WithdrawalDepositFormStatusValue =
  (typeof WithdrawalDepositFormStatus)[keyof typeof WithdrawalDepositFormStatus];

export const WITHDRAWAL_DEPOSIT_TERMINAL_STATUSES = new Set<string>([
  WithdrawalDepositFormStatus.CANCELLED,
  WithdrawalDepositFormStatus.REJECTED,
  WithdrawalDepositFormStatus.FAILED,
  WithdrawalDepositFormStatus.COMPLETED,
]);

/** Active approval stages (operation approval retired; legacy history still uses operation_*). */
export const WithdrawalDepositApprovalLevel = {
  FINANCE: "finance",
  TRUSTEE: "trustee",
} as const;

export type WithdrawalDepositApprovalLevelValue =
  (typeof WithdrawalDepositApprovalLevel)[keyof typeof WithdrawalDepositApprovalLevel];

/** Audit actions written to withdrawal_deposit_approval_history.action */
export const WithdrawalDepositHistoryAction = {
  SYNC_FAILED: "sync_failed",
  COMPENSATED: "compensated",
  BALANCE_REVERSED: "balance_reversed",
  LEDGER_CREATED: "ledger_created",
  OPERATION_APPROVED: "operation_approved",
  FINANCE_APPROVED: "finance_approved",
  TRUSTEE_APPROVED: "trustee_approved",
  SUBMIT_FOR_APPROVAL: "submit_for_approval",
  APPROVAL_REQUESTED: "approval_requested",
  AWAITING_OPERATION: "awaiting_operation",
  AWAITING_FINANCE: "awaiting_finance",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
  IDEMPOTENCY_RECORD: "idempotency_record",
  DUPLICATE_APPROVAL_SKIPPED: "duplicate_approval_skipped",
} as const;

export type WithdrawalDepositHistoryActionValue =
  (typeof WithdrawalDepositHistoryAction)[keyof typeof WithdrawalDepositHistoryAction];

/** Newer history rows that clear an older sync_failed / failure_retry compensated alert. */
export const WITHDRAWAL_DEPOSIT_FAILURE_RECOVERY_ACTIONS = new Set<string>([
  WithdrawalDepositHistoryAction.OPERATION_APPROVED,
  WithdrawalDepositHistoryAction.FINANCE_APPROVED,
  WithdrawalDepositHistoryAction.TRUSTEE_APPROVED,
  WithdrawalDepositHistoryAction.LEDGER_CREATED,
  WithdrawalDepositHistoryAction.SUBMIT_FOR_APPROVAL,
  WithdrawalDepositHistoryAction.REJECTED,
  WithdrawalDepositHistoryAction.CANCELLED,
]);

export const WithdrawalDepositLedgerStatus = {
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  NOT_PROCESSED: "not_processed",
} as const;

export const WithdrawalDepositRejectDialogMode = {
  REJECT: "reject",
  CANCEL: "cancel",
} as const;

export type WithdrawalDepositRejectDialogModeValue =
  (typeof WithdrawalDepositRejectDialogMode)[keyof typeof WithdrawalDepositRejectDialogMode];

/** Human-readable list/detail status labels in the UI. */
export const WithdrawalDepositUiStatusLabel = {
  REQUESTED: "Requested",
  AWAITING_FINANCE: "Awaiting finance",
  AWAITING_TRUSTEE: "Awaiting trustee",
  COMPLETED: "Completed",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  FAILED: "Failed",
} as const;

/** Toast copy keyed by approval stage (loading + default success). */
export const WITHDRAWAL_DEPOSIT_APPROVAL_TOAST = {
  [WithdrawalDepositApprovalLevel.FINANCE]: {
    loading: "Syncing finance approval with Salesforce…",
    success: "Confirmed by finance. Trustee can now give final approval.",
    permissionError: "Only a Finance Admin can confirm at this stage.",
  },
  [WithdrawalDepositApprovalLevel.TRUSTEE]: {
    loading: "Completing trustee approval and updating balance…",
    success: "Confirmed by trustee. Request is completed.",
    permissionError:
      "Only a trustee portal user can give final approval, after Finance has approved.",
  },
} as const;
